import { TeamSchedule } from "@swivu/team-schedule";
import { createBrowserRouter, redirect } from "react-router-dom";
import Providers from "../lib/Providers.js";
import MainLayout from "../layouts/MainLayout.js";
import Login from "./Login.js";
import NotEnabled from "./NotEnabled.js";
import FourOhFour from "./404.js";
import UnderConstruction from "@swivu/shared-ui/src/components/UnderConstruction.js";
import { Suspense } from "react";
import ViewResources from "../components/ViewResources.js";
import Resource from "./Resource.js";
import { ErrorBoundary } from "@swivu/shared-ui";
import ViewEvents from "../components/ViewEvents.js";
import LoadingSkeleton from "../components/LoadingSkeleton.js";
import ValidatedLayout from "../layouts/ValidatedLayout.js";
import ViewChecklists from "../components/ViewChecklists.js";
import ViewChecklistItem from "../components/ViewChecklistItem.js";
import ViewContacts from "../components/ViewContacts.js";
import ViewContact from "../components/ViewContact.js";
import ViewReminders from "../components/ViewReminders.js";
import ViewUsers from "../components/ViewUsers.js";
import ViewClientsPublic from "../components/ViewClients.js";
import ViewResource from "../components/ViewResource.js";
import ViewTasksPublic from "../components/ViewTasks.js";
import ViewTaskPublic from "../components/ViewTask.js";
import ViewFile from "../components/ViewFile.js";

const publicEntities = [
  "checklists",
  "clients",
  "contacts",
  "events,",
  "reminders",
  "resources",
  "tasks",
  "tasks-budget",
  "users",
  "schedule"
] as const;

export const router = createBrowserRouter([
  {
    ErrorBoundary,
    children: [
      {
        path: "/schedule/:slug/file/:file_id",
        loader: ({ params }) => {
          console.log(params);
          if (!params.file_id || !params.slug) {
            return redirect("/404");
          }
          return redirect(`/p/${params.slug}/file/${params.file_id}`);
        }
      },
      {
        path: "/hq/public/:slug/:entity/:id",
        loader: ({ params }) => {
          if (!params.entity || !params.id || !params.slug) {
            return redirect("/404");
          }
          if (
            !publicEntities.includes(
              params.entity as (typeof publicEntities)[number]
            )
          ) {
            return redirect("/404");
          }
          return redirect(`/p/${params.slug}/${params.entity}/${params.id}`);
        }
      },
      {
        element: <Providers />,
        children: [
          {
            element: <MainLayout />,
            children: [
              {
                path: "/public/*",
                loader: ({ request }) => {
                  const url = new URL(request.url);
                  const publicPathMatch =
                    url.pathname.match(/^\/public\/(.*)$/);
                  if (publicPathMatch) {
                    return redirect(`/p/${publicPathMatch[1]}`);
                  }
                  return null;
                }
              },
              {
                path: "/notEnabled",
                element: <NotEnabled />
              },
              {
                path: "/login",
                element: <Login />
              },
              {
                path: "/p/:slug",
                element: (
                  <Suspense fallback={<LoadingSkeleton />}>
                    <ErrorBoundary>
                      <ValidatedLayout />
                    </ErrorBoundary>
                  </Suspense>
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingSkeleton />}>
                        <ErrorBoundary>
                          <div className=" max-w-5xl pt-2 md:pt-4   h-full -mx-2 ">
                            <TeamSchedule
                              slideoutContainerId={"slideoutContainer"}
                              isPublicSchedule={true}
                              timezone={
                                Intl.DateTimeFormat().resolvedOptions()
                                  .timeZone ?? "America/New_York"
                              }
                            />
                          </div>
                        </ErrorBoundary>
                      </Suspense>
                    )
                  },
                  {
                    path: "file/:file_id",
                    element: <ViewFile />
                  },
                  {
                    path: "checklist/:checklist_id",
                    element: <ViewChecklistItem />
                  },
                  {
                    path: "contacts/:id",
                    children: [
                      {
                        index: true,
                        element: <ViewContacts />
                      },
                      {
                        path: "contact/:contact_id",
                        element: <ViewContact />
                      }
                    ]
                  },

                  {
                    path: "resources/:id",
                    children: [
                      {
                        index: true,
                        element: <ViewResources />
                      },
                      {
                        path: "resource/:resource_id",
                        element: <ViewResource />
                      }
                    ]
                  },
                  {
                    path: "checklists/:id",
                    children: [
                      {
                        index: true,
                        element: <ViewChecklists />
                      },
                      {
                        path: "checklist/:checklist_id",
                        element: <ViewChecklistItem />
                      }
                    ]
                  },
                  {
                    path: "tasks/:id",
                    children: [
                      {
                        index: true,
                        element: <ViewTasksPublic />
                      },
                      {
                        path: "task/:task_id",
                        element: <ViewTaskPublic />
                      }
                    ]
                  },
                  {
                    path: "events/:id",
                    element: <ViewEvents />
                  },
                  {
                    path: "users/:id",
                    element: <ViewUsers />
                  },
                  {
                    path: "reminders/:id",
                    element: <ViewReminders />
                  },
                  {
                    path: "clients/:id",
                    element: <ViewClientsPublic />
                  },
                  {
                    path: "*",
                    element: (
                      <div className=" max-w-screen-lg mx-auto pt-4">
                        <UnderConstruction
                          message={`We do not have a shared view for ${window.location.pathname
                            .split("/")
                            .at(3)}... yet.`}
                        />
                      </div>
                    )
                  }
                ]
              },
              // the schedule route remains to work with team scheduler's internal links
              {
                path: "/schedule/:slug",
                element: (
                  <Suspense fallback={<LoadingSkeleton />}>
                    <ErrorBoundary>
                      <ValidatedLayout />
                    </ErrorBoundary>
                  </Suspense>
                ),
                children: [
                  {
                    index: true,
                    loader: ({ params }) => {
                      return redirect(`/p/${params.slug}`);
                    }
                  },
                  {
                    path: "task/:task_id",
                    element: <ViewTaskPublic />
                  },
                  {
                    path: "resource/:resourceId",
                    element: (
                      <Suspense fallback={<LoadingSkeleton />}>
                        <ErrorBoundary>
                          <Resource />
                        </ErrorBoundary>
                      </Suspense>
                    )
                  }
                ]
              },

              {
                path: "*",
                element: <FourOhFour />
              }
            ]
          }
        ]
      }
    ]
  }
]);
